import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Camera, CheckCircle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function PublicGuestUpload() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [caption, setCaption] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [success, setSuccess] = useState(false);

  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const loadData = (pass = "") => {
    const headers = pass ? { "X-Wedding-Password": pass } : {};
    api.get(`/public/wedding/${slug}`, { headers })
      .then((r) => {
        setWedding(r.data?.wedding);
        setNeedsPassword(false);
        setPasswordError("");
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          setNeedsPassword(true);
          if (pass) setPasswordError("Mot de passe incorrect");
        } else {
          toast.error("Mariage introuvable");
        }
      });
  };

  useEffect(() => {
    loadData();
  }, [slug]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Le fichier ne doit pas dépasser 10 Mo");
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner une photo");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("uploaded_by", guestName || "Invité");
      formData.append("caption", caption || "");

      const headers = {};
      if (password) headers["X-Wedding-Password"] = password;
      const guestUploadToken = searchParams.get("token");
      if (!guestUploadToken) {
        toast.error("Ce lien de partage est invalide ou a expiré.");
        setLoading(false);
        return;
      }
      headers["X-Guest-Upload-Token"] = guestUploadToken;
      
      const res = await api.post(`/public/wedding/${slug}/photos/upload`, formData, {
        headers
      });
      setSuccess(true);
      setSelectedFile(null);
      setPreviewUrl("");
      setCaption("");
      toast.success(res.data.status === "pending" ? "Photo envoyée pour modération !" : "Photo ajoutée à la galerie ! ✨");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'importation");
    } finally {
      setLoading(false);
    }
  };

  if (needsPassword) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white p-10 rounded-2xl shadow-xl border border-stone-200 max-w-md w-full text-center">
          <div className="text-4xl mb-6">🔒</div>
          <h2 className="font-serif text-3xl text-charcoal mb-2">Accès Privé</h2>
          <p className="text-sm text-stone-500 font-light mb-8">Veuillez entrer le mot de passe pour accéder à cet espace.</p>
          <form onSubmit={(e) => { e.preventDefault(); loadData(password); }} className="space-y-4">
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe" 
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-sm text-center focus:outline-none focus:border-sage"
            />
            {passwordError && <div className="text-red-500 text-xs">{passwordError}</div>}
            <button type="submit" className="w-full bg-charcoal text-white py-3 rounded-sm font-label-caps text-xs tracking-widest uppercase hover:bg-stone-800 transition-colors">
              Valider
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment">
        <div className="font-serif text-xl text-charcoal-soft animate-pulse">Chargement...</div>
      </div>
    );
  }

  const coupleNames = `${wedding.bride_name} & ${wedding.groom_name}`;

  return (
    <div className="min-h-screen bg-parchment text-charcoal font-sans flex flex-col justify-between">
      {/* Header */}
      <header className="px-6 py-4 bg-white/70 backdrop-blur-md border-b border-border-cream flex items-center gap-4">
        <Link to={`/w/${slug}`} className="text-charcoal hover:text-sage transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <div className="font-serif text-lg leading-none">{coupleNames}</div>
          <span className="text-[10px] overline text-sage tracking-[0.2em]">Partage de photos</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-md w-full mx-auto px-6 py-8 flex flex-col justify-center">
        {success ? (
          <div className="bg-white border border-border-cream p-8 text-center rounded-sm space-y-6 reveal shadow-sm">
            <div className="w-16 h-16 bg-sage/10 rounded-sm flex items-center justify-center mx-auto text-sage">
              <CheckCircle size={32} />
            </div>
            <div className="space-y-2">
              <h2 className="font-serif text-3xl font-light">Merci infiniment !</h2>
              <p className="text-sm text-charcoal-soft font-light leading-relaxed">
                Votre photo a été importée avec succès et enrichit désormais la galerie de souvenirs de {coupleNames}.
              </p>
            </div>
            <button 
              onClick={() => setSuccess(false)}
              className="btn-sage w-full py-3.5 text-xs font-bold uppercase tracking-wider rounded-sm"
            >
              Envoyer une autre photo
            </button>
            <Link 
              to={`/w/${slug}`}
              className="block text-xs uppercase tracking-widest font-semibold text-sage hover:text-sage/80 transition-colors pt-2"
            >
              Retourner sur le site
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-border-cream p-6 lg:p-8 rounded-sm shadow-sm space-y-6 reveal">
            <div className="text-center space-y-2">
              <h2 className="font-serif text-3xl font-light">Enrichir la Galerie</h2>
              <p className="text-xs text-charcoal-soft font-light leading-relaxed">
                Capturez ou importez un instant magique de cette belle célébration pour l'offrir aux mariés.
              </p>
            </div>

            <form onSubmit={handleUpload} className="space-y-5">
              {/* Photo selector box */}
              <div className="relative">
                {previewUrl ? (
                  <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-border-cream">
                    <img src={previewUrl} alt="Aperçu" className="w-full h-full object-cover" />
                    <button 
                      type="button"
                      onClick={() => { setSelectedFile(null); setPreviewUrl(""); }}
                      className="absolute top-2 right-2 bg-charcoal/80 hover:bg-charcoal text-parchment text-xs px-2.5 py-1 rounded-sm transition-colors"
                    >
                      Effacer
                    </button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-border-cream hover:border-sage bg-parchment/50 transition-colors py-12 flex flex-col items-center justify-center cursor-pointer rounded-sm group">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange} 
                      className="hidden" 
                    />
                    <div className="w-12 h-12 bg-sage/5 group-hover:bg-sage/10 text-sage flex items-center justify-center rounded-sm transition-colors mb-4">
                      <Camera size={22} strokeWidth={1.5} />
                    </div>
                    <span className="text-xs font-medium text-charcoal mb-1">Prendre ou choisir une photo</span>
                    <span className="text-[10px] text-charcoal-soft font-light">Max 10 Mo (JPG, PNG, WEBP)</span>
                  </label>
                )}
              </div>

              {/* Input name */}
              <label className="block">
                <span className="overline text-[10px] text-charcoal-soft font-semibold">Votre prénom & nom</span>
                <input 
                  type="text"
                  placeholder="Ex: Jean-Luc Martin"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors"
                />
              </label>

              {/* Input caption */}
              <label className="block">
                <span className="overline text-[10px] text-charcoal-soft font-semibold">Légende poétique</span>
                <input 
                  type="text"
                  placeholder="Ex: L'arrivée sous les fleurs sauvages... (facultatif)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors"
                />
              </label>

              <button 
                type="submit"
                disabled={loading || !selectedFile}
                className="btn-sage w-full py-3.5 text-xs font-bold uppercase tracking-widest rounded-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Importation en cours..." : "Partager cette photo ✨"}
              </button>
            </form>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="px-6 py-6 text-center text-[9px] uppercase tracking-[0.2em] text-charcoal-soft opacity-60">
        © 2026 Ceremind — Galerie Partagée
      </footer>
    </div>
  );
}
